// FieldMap — Leaflet street map + SVG overlay for markers, GPS, place labels.
//
// Wraps Leaflet's imperative API in a React component. Caller passes:
//   region : FieldRegion       — center / zoom / bounds (a /regions doc)
//   markers: FieldMarker[]     — already filtered to one region by caller
//   gps    : FieldGps          — user's current GPS reading
//   users  : FieldUser[]       — used for admin label "01" on each pin
//   mode   : 'user' | 'admin'
//   stadiaApiKey
//
// Admin mode: clicking the map calls onPlace(lat, lng); pending pin renders a
// crosshair until confirmed.
//
// Visual edge dissolves to paper via .gb-map CSS mask (see leaflet.css).

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./leaflet.css";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { T } from "../tokens";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { PLACE_LABELS, type PlaceLabel } from "./PLACE_LABELS";
import type {
  FieldGps,
  FieldMapMode,
  FieldMarker,
  FieldUser,
} from "./types";

const TILE_ATTRIBUTION =
  "© Stadia Maps © OpenMapTiles © OpenStreetMap";

// Learner-facing tolerance radius (meters). Mirrors ARRIVED_M in
// apps/user/src/routes/MapView.tsx — when a learner is within this
// many meters of a pin, the「我 找 到 了」CTA becomes tappable.
// We paint a soft circle of this radius around each unfound pin in user
// mode so that learners with iffy GPS can see "I'm inside the green
// zone — close enough to confirm" instead of wandering chasing the
// exact center pixel.
const PIN_TOLERANCE_M = 40;

// How much to grow the configured region bounds when computing the actual
// pannable maxBounds. The region doc defines "where pins live"; this
// inflate gives learners headroom to walk past the configured area
// without the map snapping back (and lets a GPS dot near the configured
// corner stay visible with margin around it).
//
// 0.6 = each side grows 60% of the original span (total area ~2.56×).
// Bumped up because learners reported the original bounds clipping them
// when they walked even 50m past an edge.
const BOUNDS_INFLATE = 0.6;

function inflateBounds(
  swLat: number,
  swLng: number,
  neLat: number,
  neLng: number,
  factor: number,
): { swLat: number; swLng: number; neLat: number; neLng: number } {
  const padLat = (neLat - swLat) * factor;
  const padLng = (neLng - swLng) * factor;
  return {
    swLat: swLat - padLat,
    swLng: swLng - padLng,
    neLat: neLat + padLat,
    neLng: neLng + padLng,
  };
}

export interface FieldMapProps {
  /**
   * Region geometry as primitive props (NOT a single object reference).
   *
   * Why primitives, not `region: FieldRegion`: this component is fed by
   * Firestore live subscriptions that re-emit fresh object + array
   * references every tick even when values are unchanged. Depending on
   * `region.center` (an array) would re-run effects on every snapshot,
   * dragging the map back to the region centre while the user is panning.
   * Primitives compare with === — same number, no effect re-run, no false
   * positives. The bug never has a chance to exist.
   */
  regionId: string;
  regionCenterLat: number;
  regionCenterLng: number;
  regionZoom: number;
  regionBoundsSwLat: number;
  regionBoundsSwLng: number;
  regionBoundsNeLat: number;
  regionBoundsNeLng: number;
  markers: FieldMarker[];
  /** Optional — admin pin label by user.no */
  users?: FieldUser[];
  gps?: FieldGps | null;
  mode?: FieldMapMode;
  stadiaApiKey: string;
  /** Admin click handler: tap on map → lat/lng */
  onPlace?: (lat: number, lng: number) => void;
  /** Worker / learner marker tap */
  onTapMarker?: (marker: FieldMarker) => void;
  /** Tap on empty map area (any mode). Marker taps stopPropagation, so this
   *  only fires for blank-space taps — useful for dismissing popovers. */
  onTapMap?: () => void;
  /** Pending pin (admin) — shows crosshair before confirm */
  pendingPin?: { lat: number; lng: number } | null;
  /** Highlight a single marker by id (learner's own pin) */
  highlightMarkerId?: string | null;
  /** User-only filter: drop markers that aren't this user's */
  showOnlyForUser?: string | null;
  /** Compass heading in degrees (0=N, 90=E). When provided alongside `gps`
   *  in user mode, the GPS marker renders a directional cone like Google
   *  Maps. Null/undefined = hide cone, plain crosshair. */
  heading?: number | null;
  /** GPS accuracy in meters (radius of the 68% confidence circle from
   *  navigator.geolocation). When provided, the marker draws a faint
   *  ring at that radius so the learner knows "I'm somewhere in here".
   *  Null/undefined = no ring. */
  gpsAccuracy?: number | null;
  /** Called when user taps the recenter button. Useful for the consumer
   *  to trigger DeviceOrientation permission request on the same gesture
   *  (iOS gates the API behind a user tap). */
  onRecenterTap?: () => void;
  /** User-mode controls bottom offset override. Default is the legacy
   *  `calc(196px + safe-area-inset-bottom)` magic that lifts buttons
   *  above the old PromptSheet + UserNav + clue card stack. Pass a
   *  CSS length (e.g. `"calc(var(--bar-h-rail) + 12px + ...)"`) to
   *  align controls to whatever bar height the consumer is rendering. */
  userControlsBottom?: string;
  /** When this point changes to a non-null value, the map pans to it
   *  once (animated, 600ms). Caller is responsible for resetting it to
   *  null (or to a new value) before triggering again. Used by admin
   *  「· 藏 一 個」flow to recenter on GPS after setPending so the
   *  pending crosshair doesn't appear off-screen. Learner side leaves
   *  this undefined; the effect early-returns and has zero impact. */
  focusPoint?: { lat: number; lng: number } | null;
  /** 2026-05-30 admin-only: learner live positions to overlay on the map
   *  (worker「顯示學員」toggle). Distinct dot style from pins. The learner
   *  app never passes this → zero impact there. */
  learnerDots?: Array<{
    userId: string;
    name: string;
    lat: number;
    lng: number;
    ago?: string;
  }>;
  /** Override default height (full-height when in a flex column) */
  style?: React.CSSProperties;
}

export function FieldMap({
  regionId,
  regionCenterLat,
  regionCenterLng,
  regionZoom,
  regionBoundsSwLat,
  regionBoundsSwLng,
  regionBoundsNeLat,
  regionBoundsNeLng,
  markers,
  users = [],
  gps,
  mode = "user",
  stadiaApiKey,
  onPlace,
  onTapMarker,
  onTapMap,
  pendingPin,
  highlightMarkerId = null,
  showOnlyForUser = null,
  heading = null,
  gpsAccuracy = null,
  onRecenterTap,
  userControlsBottom = "calc(196px + env(safe-area-inset-bottom, 0px))",
  focusPoint = null,
  learnerDots,
  style,
}: FieldMapProps) {
  const reducedMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [overlaySize, setOverlaySize] = useState({ w: 0, h: 0 });
  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  const visibleMarkers = useMemo(() => {
    return markers.filter((m) => {
      if (m.regionId !== regionId) return false;
      if (mode === "user" && showOnlyForUser && m.userId !== showOnlyForUser)
        return false;
      return true;
    });
  }, [markers, regionId, mode, showOnlyForUser]);

  const userById = useMemo(() => {
    const out: Record<string, FieldUser> = {};
    for (const u of users) out[u.id] = u;
    return out;
  }, [users]);

  // ── Initialize Leaflet once on mount ─────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // PR-B3-1 (D7): Stadia Alidade Smooth has limited OSM data in 瑞芳;
    // zoom > 19 just pixel-stretches. Keep Stadia as default (consistent
    // 灰階紙墨 styling) but set maxNativeZoom 19 + maxZoom 22 so Leaflet
    // upscales beyond native tile data for centimeter-level pin placement.
    // OSM raw fallback removed (color too saturated, breaks 純灰階 system).
    const tileUrl = `https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${stadiaApiKey}`;

    const inflated = inflateBounds(
      regionBoundsSwLat,
      regionBoundsSwLng,
      regionBoundsNeLat,
      regionBoundsNeLng,
      BOUNDS_INFLATE,
    );
    const m = L.map(containerRef.current, {
      center: [regionCenterLat, regionCenterLng],
      zoom: regionZoom,
      maxBounds: L.latLngBounds(
        [inflated.swLat, inflated.swLng],
        [inflated.neLat, inflated.neLng],
      ),
      // Viscosity 0.7 (was 1.0): users can drag a hair past the edge but
      // the map gently snaps back. 1.0 was a hard wall — even a GPS dot
      // 5m past the corner couldn't be reached.
      maxBoundsViscosity: 0.7,
      zoomControl: false,
      attributionControl: false,
      dragging: true,
      doubleClickZoom: false,
      scrollWheelZoom: false,
      keyboard: false,
      touchZoom: true,
      boxZoom: false,
    });
    // Day 14: detectRetina was making @2x tiles render at half size on
    // high-DPI screens — beautiful but the road labels felt too tiny on
    // mobile during field recon. Turning it off uses the standard tile at
    // its natural size, so labels (and everything) read about 2x bigger on
    // retina devices. Tiles look a touch less crisp but still sharp enough.
    L.tileLayer(tileUrl, {
      maxZoom: 22,
      maxNativeZoom: 19,
      minZoom: 12,
      detectRetina: false,
      attribution: TILE_ATTRIBUTION,
    }).addTo(m);
    mapRef.current = m;

    // Throttle SVG overlay re-render to one per animation frame.
    // Leaflet emits "move" 30-60 times per second during a drag; without
    // a rAF gate React reconciles the entire marker SVG that often, which
    // tanks fps on mid-range phones (especially with place labels +
    // fog veil in the same overlay). One rAF per frame is the natural
    // upper bound — beyond that the browser hasn't even painted yet.
    let rafPending = false;
    const rerender = () => {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(() => {
        rafPending = false;
        forceRender();
      });
    };
    m.on("move", rerender);
    m.on("zoom", rerender);

    const size = m.getSize();
    setOverlaySize({ w: size.x, h: size.y });
    m.on("resize", () => {
      const s = m.getSize();
      setOverlaySize({ w: s.x, h: s.y });
    });

    // give layout one frame to settle before locking tile size
    const t = window.setTimeout(() => m.invalidateSize(), 30);

    return () => {
      window.clearTimeout(t);
      m.remove();
      mapRef.current = null;
    };
    // intentionally mount-only — region changes handled in next effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Click handler: admin placement + generic onTapMap ────────────────
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const handler = (e: L.LeafletMouseEvent) => {
      if (mode === "admin" && onPlace) {
        onPlace(e.latlng.lat, e.latlng.lng);
      }
      onTapMap?.();
    };
    m.on("click", handler);
    return () => {
      m.off("click", handler);
    };
  }, [mode, onPlace, onTapMap]);

  // ── React to region change ───────────────────────────────────────────
  // All deps are primitives (number / string), so === comparison gates
  // re-runs precisely on real value changes. Firestore can re-emit
  // snapshots all day; if the lat/lng numbers haven't actually changed,
  // this effect doesn't fire. No ref hacks needed.
  const firstGpsAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const inflated = inflateBounds(
      regionBoundsSwLat,
      regionBoundsSwLng,
      regionBoundsNeLat,
      regionBoundsNeLng,
      BOUNDS_INFLATE,
    );
    m.setMaxBounds(
      L.latLngBounds(
        [inflated.swLat, inflated.swLng],
        [inflated.neLat, inflated.neLng],
      ),
    );
    m.setView([regionCenterLat, regionCenterLng], regionZoom);
    // Stage 11: 不再 reset firstGpsAppliedRef = null。手動切區後使用者
    // 想看那個 region 的 center、不要又被 GPS 拉回。Initial mount 時
    // ref 還是 null、與 regionId(string)永不相等、first-GPS effect 仍
    // 會跑一次。
  }, [
    regionId,
    regionCenterLat,
    regionCenterLng,
    regionZoom,
    regionBoundsSwLat,
    regionBoundsSwLng,
    regionBoundsNeLat,
    regionBoundsNeLng,
  ]);

  // ── First GPS fix → centre on user (both modes) ─────────────────────
  // Geolocation is async; on /map mount the map starts at region center,
  // 1-3s later the first fix comes back. Snap to the user once per
  // region so the map opens on "where I am" instead of the region's
  // arbitrary centre. Subsequent fixes don't re-snap — that would fight
  // the user's manual panning.
  //
  // Stage 11: removed the `mode === "user"` gate so the admin map also
  // centers on the worker. Switched from panTo (animated 600ms) to
  // setView (instant) — initial centering should feel like the map was
  // already positioned, not animated into place.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !gps) return;
    if (firstGpsAppliedRef.current === regionId) return;
    firstGpsAppliedRef.current = regionId;
    // Only snap if the fix is inside the region bounds — otherwise we'd
    // yank the map outside maxBounds and trigger Leaflet's snap-back,
    // which looks like a flicker.
    const bounds = L.latLngBounds(
      [regionBoundsSwLat, regionBoundsSwLng],
      [regionBoundsNeLat, regionBoundsNeLng],
    );
    const target = L.latLng(gps.lat, gps.lng);
    if (bounds.contains(target)) {
      m.setView(target, m.getZoom(), { animate: false });
    }
  }, [
    gps,
    regionId,
    regionBoundsSwLat,
    regionBoundsSwLng,
    regionBoundsNeLat,
    regionBoundsNeLng,
  ]);

  // ── External focus point → pan map once ──────────────────────────────
  // Admin「· 藏 一 個」flow sets focusPoint to gps coords after a
  // setPending; without this the pending crosshair can render off-screen
  // when GPS is outside the current map viewport. We watch the lat/lng
  // primitives (not the object identity) so Firestore-style re-renders
  // don't trigger spurious pans. Caller resets focusPoint to null when
  // PinForm closes; setting it to the same value again triggers a fresh
  // pan because we depend on the primitives.
  useEffect(() => {
    if (!focusPoint) return;
    const m = mapRef.current;
    if (!m) return;
    // Pan the focus point, then nudge it UP into the visible upper area so
    // the PinForm bottom sheet (which covers the lower ~half) doesn't hide
    // the pending crosshair. panBy([0, +Y]) scrolls the map content up →
    // the point moves toward the top. (2026-05-30: bumped 150→230 because the
    // 「藏其他碎片」form is much taller — at 150 the tapped pin sat behind it.
    // 230 clears the taller form; for the short form it just sits a bit higher,
    // which is fine.)
    const FORM_CLEARANCE_PX = 230;
    m.panTo([focusPoint.lat, focusPoint.lng], {
      animate: false,
    });
    m.panBy([0, FORM_CLEARANCE_PX], { animate: true, duration: 0.6 });
  }, [focusPoint?.lat, focusPoint?.lng]);

  // ── Helper: lat/lng → container pixel for SVG overlay ────────────────
  const project = (lat: number, lng: number) => {
    const m = mapRef.current;
    if (!m) return null;
    const p = m.latLngToContainerPoint([lat, lng]);
    return { x: p.x, y: p.y };
  };

  const placeLabels: PlaceLabel[] = PLACE_LABELS[regionId] ?? [];

  return (
    <div
      style={{
        flex: 1,
        position: "relative",
        overflow: "hidden",
        background: T.paper,
        ...style,
      }}
    >
      <div
        ref={containerRef}
        className={`gb-map ${mode === "admin" ? "gb-map-admin" : "gb-map-user"}`}
        style={{ position: "absolute", inset: 0 }}
      />

      {/* SVG overlay: markers + place labels + GPS, drawn above tiles */}
      <svg
        style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 6 }}
        width={overlaySize.w}
        height={overlaySize.h}
      >
        {/* Region-bounds fog veil — paints a near-white wash over the
            area OUTSIDE the configured region.bounds, leaving the
            "walkable" core fully visible. Tells the learner "this is
            where today happens; everything past is scenery."
            Single even-odd path: outer rect = viewport, inner rect =
            projected region.bounds. No SVG filter (Gaussian blur on a
            viewport-sized path tanked fps during pan on iPhone XR
            class hardware — confirmed Day 14b). The hard edge is
            softened visually by the low alpha (0.62) and the 1px ink
            hairline outlining the inner rect, which reads as "this is
            the line" rather than "the world ends here".
            pointerEvents:none keeps panning / pin taps live. */}
        {(() => {
          const w = overlaySize.w;
          const h = overlaySize.h;
          if (w <= 0 || h <= 0 || !mapRef.current) return null;
          const sw = project(regionBoundsSwLat, regionBoundsSwLng);
          const ne = project(regionBoundsNeLat, regionBoundsNeLng);
          if (!sw || !ne) return null;
          const x1 = Math.min(sw.x, ne.x);
          const x2 = Math.max(sw.x, ne.x);
          const y1 = Math.min(sw.y, ne.y);
          const y2 = Math.max(sw.y, ne.y);
          const PAD = 40;
          return (
            <g pointerEvents="none">
              <path
                d={`M ${-PAD} ${-PAD} H ${w + PAD} V ${h + PAD} H ${-PAD} Z
                    M ${x1} ${y1} H ${x2} V ${y2} H ${x1} Z`}
                fillRule="evenodd"
                fill="rgba(250,250,250,0.62)"
              />
              <rect
                x={x1}
                y={y1}
                width={x2 - x1}
                height={y2 - y1}
                fill="none"
                stroke="rgba(14,14,14,0.18)"
                strokeWidth="1"
              />
            </g>
          );
        })()}

        {/* Place labels */}
        {placeLabels.map((p, i) => {
          const pt = project(p.lat, p.lng);
          if (!pt) return null;
          const isStation = /車\s*站/.test(p.zh);
          // Day 14: place names felt too small on mobile during field recon.
          // Bumped station 14 → 17, general place 9-13 → 12-16.
          const fs = isStation ? 17 : 12 + (p.weight ?? 0.85) * 4;
          const labelFill = isStation
            ? "#0E0E0E"
            : (p.weight ?? 0) >= 0.8
              ? "#2A2A2A"
              : "#6B6B6B";
          return (
            <g key={i}>
              {isStation ? (
                <g transform={`translate(${pt.x},${pt.y})`}>
                  <circle r="12" fill="#FAFAFA" opacity="0.96" />
                  <circle r="10" fill="#0E0E0E" />
                  <path
                    d="M -5 -3 Q -5 -4 -4 -4 L 4 -4 Q 5 -4 5 -3 L 5 1.5 L -5 1.5 Z"
                    fill="#FAFAFA"
                  />
                  <rect x="-3.4" y="-2.8" width="6.8" height="2" rx="0.4" fill="#0E0E0E" />
                  <line x1="-5" y1="1.6" x2="5" y2="1.6" stroke="#FAFAFA" strokeWidth="0.6" />
                  <circle cx="-3" cy="3" r="1.4" fill="#FAFAFA" />
                  <circle cx="3" cy="3" r="1.4" fill="#FAFAFA" />
                  <circle cx="-3" cy="3" r="0.55" fill="#0E0E0E" />
                  <circle cx="3" cy="3" r="0.55" fill="#0E0E0E" />
                </g>
              ) : (
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r="1.8"
                  fill={(p.weight ?? 0) >= 0.95 ? "#0E0E0E" : "#6B6B6B"}
                  opacity="0.85"
                />
              )}
              {/* halo + text */}
              <text
                x={pt.x + (isStation ? 14 : 8)}
                y={pt.y + 5}
                fontFamily="Noto Sans TC, sans-serif"
                fontSize={fs}
                fontWeight={isStation ? 700 : 400}
                letterSpacing="0.3"
                fill="#FAFAFA"
                stroke="#FAFAFA"
                strokeWidth="4"
                strokeLinejoin="round"
                paintOrder="stroke"
                textAnchor="start"
                opacity="0.96"
              >
                {p.zh}
              </text>
              <text
                x={pt.x + (isStation ? 14 : 8)}
                y={pt.y + 5}
                fontFamily="Noto Sans TC, sans-serif"
                fontSize={fs}
                fontWeight={isStation ? 700 : 400}
                letterSpacing="0.3"
                fill={isStation ? "#0E0E0E" : labelFill}
                textAnchor="start"
              >
                {p.zh}
              </text>
            </g>
          );
        })}

        {/* PR-B3-3 (D4): pin clustering by screen distance. Project each
            visible marker to container px, group markers whose screen
            distance < 16px into single "cluster" pin with mono count.
            Recomputed on every map move/zoom (forceRender fires both). */}
        {(() => {
          const CLUSTER_PX = 16;
          interface Projected {
            m: (typeof visibleMarkers)[number];
            pt: { x: number; y: number };
          }
          const projected: Projected[] = [];
          for (const m of visibleMarkers) {
            const pt = project(m.lat, m.lng);
            if (pt) projected.push({ m, pt });
          }
          // Greedy single-pass clustering: walk projected, group each into
          // first cluster whose centroid is within CLUSTER_PX, otherwise new.
          interface Cluster {
            members: Projected[];
            cx: number;
            cy: number;
          }
          const clusters: Cluster[] = [];
          for (const p of projected) {
            let matched: Cluster | null = null;
            for (const c of clusters) {
              const dx = c.cx - p.pt.x;
              const dy = c.cy - p.pt.y;
              if (dx * dx + dy * dy < CLUSTER_PX * CLUSTER_PX) {
                matched = c;
                break;
              }
            }
            if (matched) {
              matched.members.push(p);
              // Recompute centroid (average)
              const n = matched.members.length;
              matched.cx =
                matched.members.reduce((s, x) => s + x.pt.x, 0) / n;
              matched.cy =
                matched.members.reduce((s, x) => s + x.pt.y, 0) / n;
            } else {
              clusters.push({ members: [p], cx: p.pt.x, cy: p.pt.y });
            }
          }

          return clusters.map((c) => {
            const isCluster = c.members.length > 1;
            // Hit target: tap on cluster routes to first member; UX will
            // teach learners to zoom in to disambiguate (cluster expands).
            const lead = c.members[0].m;
            const highlighted = c.members.some(
              (p) => p.m.id === highlightMarkerId,
            );
            const u = userById[lead.userId];
            const key = isCluster
              ? `cluster-${c.members.map((p) => p.m.id).join("|")}`
              : lead.id;
            // Day 14: marker = filled circle (with 1/2/3 label) + a small
            // triangle hanging below pointing to the actual lat/lng on the
            // ground. The geometric anchor point (lat/lng) is the tip of the
            // triangle, so the circle floats just above it.
            //   - 1/2/3 ordinal label distinguishes pins (worker can hint
            //     "you still haven't found pin 2").
            //   - Found pins render hollow + no ripple ("已找到、安靜了").
            const isFound = !isCluster && lead.found === true;
            // Cluster head is the INNER solid (r=9); a hairline outer ring
            // (CLUSTER_OUTER_R) is added below so a cluster reads as "a group
            // ringed together", unmistakably different from a single pin
            // (solid circle + ordinal + triangle tail). 2026-05-30 fix for
            // the "is this 1 pin numbered N, or N pins clustered?" ambiguity.
            //
            // 2026-05-30 (round 2): UN-FOUND is what the learner is hunting,
            // so it must dominate — bigger (r=10) + solid + ripple. FOUND
            // recedes — smaller (r=7), thin hollow, no ripple ("done, quiet").
            // (Earlier we over-emphasised found with a thick ring + halo; the
            // hierarchy was backwards.)
            const r = isCluster
              ? 9
              : isFound
                ? 7
                : highlighted
                  ? 10
                  : 10;
            const CLUSTER_OUTER_R = 14;
            // Triangle hangs below the anchor by `triH`. The circle sits
            // above the anchor (lat/lng pivot) by `triH + r`.
            const triH = isCluster ? 0 : 7;
            const headCy = c.cy - triH - r;
            const labelText = isCluster
              ? `×${c.members.length}`
              : lead.pinIndex
                ? String(lead.pinIndex)
                : "";
            const fillColor = isFound ? "#FAFAFA" : "#0E0E0E";
            const strokeColor = "#0E0E0E";
            const textColor = isFound ? "#0E0E0E" : "#FAFAFA";
            // Tolerance ring — only on user mode, unfound, non-cluster pins.
            // Paints a soft circle at PIN_TOLERANCE_M radius around the pin's
            // actual ground location (c.cx, c.cy = the anchor, NOT headCy
            // which sits above the anchor) so learners with imperfect GPS
            // can tell "I'm in the zone, close enough to confirm". Drawn
            // first inside the <g> so the head circle / ripple / triangle
            // all paint on top.
            let toleranceRadiusPx = 0;
            if (
              mode === "user" &&
              !isCluster &&
              !isFound &&
              mapRef.current
            ) {
              const m = mapRef.current;
              const center = m.latLngToContainerPoint([lead.lat, lead.lng]);
              const lngOffset =
                PIN_TOLERANCE_M / (111_320 * Math.cos((lead.lat * Math.PI) / 180));
              const east = m.latLngToContainerPoint([
                lead.lat,
                lead.lng + lngOffset,
              ]);
              toleranceRadiusPx = Math.abs(east.x - center.x);
            }
            return (
              <g
                key={key}
                style={{ cursor: "pointer", pointerEvents: "all" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onTapMarker?.(lead);
                }}
              >
                {/* Tolerance ring (學員端、未找到的 pin):若 GPS 跑掉,
                    走進這圈就算找到、不用追到精準中心點。視覺極淡:
                    虛線描邊 + 4% 灰填充、不搶過 head circle 的存在感。 */}
                {toleranceRadiusPx > 4 && toleranceRadiusPx < 600 && (
                  <circle
                    cx={c.cx}
                    cy={c.cy}
                    r={toleranceRadiusPx}
                    fill="rgba(14,14,14,0.04)"
                    stroke="rgba(14,14,14,0.18)"
                    strokeWidth="1"
                    strokeDasharray="3 4"
                    pointerEvents="none"
                  />
                )}

                {/* Ripple — only on unfound, non-cluster pins. Centered on
                    the head circle, not the anchor, so the pulse radiates
                    from where the eye lands. */}
                {!isCluster && !isFound && [0, 1, 2].map((i) => (
                  <circle
                    key={i}
                    cx={c.cx}
                    cy={headCy}
                    r={r}
                    fill="none"
                    stroke="rgba(14,14,14,0.4)"
                    strokeWidth="1"
                  >
                    <animate
                      attributeName="r"
                      from={String(r)}
                      to="32"
                      dur="3.6s"
                      begin={`${-i * 1.2}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      from="0.7"
                      to="0"
                      dur="3.6s"
                      begin={`${-i * 1.2}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                ))}
                {/* Triangle tail — apex at the lat/lng anchor (c.cx, c.cy),
                    base sitting under the head circle. Same fill as head so
                    the pin reads as one connected shape. */}
                {!isCluster && triH > 0 && (
                  <polygon
                    points={`${c.cx - r * 0.55},${headCy + r * 0.6} ${c.cx + r * 0.55},${headCy + r * 0.6} ${c.cx},${c.cy}`}
                    fill={fillColor}
                    stroke={strokeColor}
                    strokeWidth={isFound ? 1 : 0}
                    strokeLinejoin="round"
                    opacity={isFound ? 0.5 : 1}
                  >
                    {!isFound && (
                      <animate
                        attributeName="opacity"
                        values="0.55;1;0.55"
                        dur="3.2s"
                        repeatCount="indefinite"
                      />
                    )}
                  </polygon>
                )}
                {/* Cluster outer ring — hairline concentric circle around the
                    solid inner head, so a cluster is unmistakably "a group"
                    vs a single pin's lone solid circle. No triangle tail. */}
                {isCluster && (
                  <circle
                    cx={c.cx}
                    cy={headCy}
                    r={CLUSTER_OUTER_R}
                    fill="none"
                    stroke="#0E0E0E"
                    strokeWidth="1"
                  />
                )}
                {/* Head circle — main visual anchor with the 1/2/3 label.
                    Found pins recede: small (r=7), thin 1px hollow ring,
                    dimmed to 0.5, no ripple — "done, quiet". Un-found pins
                    dominate: large (r=10) solid black + breathing ripple,
                    because those are the ones the learner is still hunting.
                    2026-05-30 (hierarchy corrected per organizer). */}
                <circle
                  cx={c.cx}
                  cy={headCy}
                  r={r}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={isFound ? 1 : 0}
                  opacity={isFound ? 0.5 : 1}
                >
                  {!isCluster && !isFound && (
                    <animate
                      attributeName="opacity"
                      values="0.55;1;0.55"
                      dur="3.2s"
                      repeatCount="indefinite"
                    />
                  )}
                </circle>
                {/* Ordinal / cluster count label. Found numeral is smaller
                    + dimmed to match the receding found pin; un-found stays
                    bold and full-contrast. */}
                {labelText && (
                  <text
                    x={c.cx}
                    y={headCy + (isFound ? 3 : 4)}
                    textAnchor="middle"
                    fontFamily="Space Mono, monospace"
                    fontSize={isCluster ? 9 : isFound ? 9 : 12}
                    fontWeight="500"
                    fill={textColor}
                    opacity={isFound ? 0.5 : 1}
                    style={{ pointerEvents: "none" }}
                  >
                    {labelText}
                  </text>
                )}
                {!isCluster && mode === "admin" && u && (
                  <text
                    x={c.cx + r + 6}
                    y={headCy + 4}
                    fontFamily="Space Mono, monospace"
                    fontSize="11"
                    letterSpacing="1.2"
                    fill="#0E0E0E"
                  >
                    {u.no}
                  </text>
                )}
                {/* Wider transparent hit target — covers head + triangle */}
                <circle
                  cx={c.cx}
                  cy={headCy + triH / 2}
                  r="26"
                  fill="transparent"
                />
              </g>
            );
          });
        })()}

        {/* Pending pin (admin) */}
        {pendingPin && (() => {
          const pt = project(pendingPin.lat, pendingPin.lng);
          if (!pt) return null;
          return (
            <g>
              <circle
                cx={pt.x}
                cy={pt.y}
                r="14"
                fill="none"
                stroke="#0E0E0E"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
              <line
                x1={pt.x}
                y1={pt.y - 22}
                x2={pt.x}
                y2={pt.y + 22}
                stroke="#0E0E0E"
                strokeWidth="1"
              />
              <line
                x1={pt.x - 22}
                y1={pt.y}
                x2={pt.x + 22}
                y2={pt.y}
                stroke="#0E0E0E"
                strokeWidth="1"
              />
            </g>
          );
        })()}

        {/* GPS crosshair — user mode: breathing cross + ripple.
            When `heading` is available (device orientation granted), a wedge
            cone fans out from the marker in the direction the phone faces —
            same affordance as Google Maps' blue beam. Without heading, the
            crosshair stays put (no cone).
            When `gpsAccuracy` is provided, draw a faint ring at the
            accuracy radius so the learner can see "I'm somewhere inside
            this circle" — solves the "GPS says I'm here but I'm not"
            anxiety on hillside drift.

            Visual language: same double-circle + center-dot the admin
            page used to ship (workers loved its readability), pulled
            into shared use 2026-05-27. Learner gets a deeper ink-900
            stroke + dashed accuracy ring + breath animation so the
            marker stays prominent against busy map tiles. 2026-05-30:
            admin now ALSO passes `heading` + requests compass permission
            on the locate-button tap, so workers see the same facing cone
            as learners (was previously user-only). */}
        {gps && (() => {
          const pt = project(gps.lat, gps.lng);
          if (!pt) return null;
          // Convert meters → pixels using leaflet's projection: pick a
          // point 1 meter east of the user, measure pixel distance.
          // Cheaper than re-deriving from zoom + latitude trig.
          let accuracyRadiusPx = 0;
          if (gpsAccuracy !== null && gpsAccuracy > 0 && mapRef.current) {
            const m = mapRef.current;
            const center = m.latLngToContainerPoint([gps.lat, gps.lng]);
            // Move ~accuracy meters east. 1 degree lng ≈ 111_320 * cos(lat) m.
            const lngOffset =
              gpsAccuracy / (111_320 * Math.cos((gps.lat * Math.PI) / 180));
            const east = m.latLngToContainerPoint([gps.lat, gps.lng + lngOffset]);
            accuracyRadiusPx = Math.abs(east.x - center.x);
          }
          return (
            <g style={{ animation: "gb-breath-3500 3500ms ease-in-out infinite" }}>
              {accuracyRadiusPx > 4 && accuracyRadiusPx < 400 && (
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r={accuracyRadiusPx}
                  fill="rgba(14,14,14,0.06)"
                  stroke="rgba(14,14,14,0.18)"
                  strokeWidth="0.5"
                  strokeDasharray="2 3"
                />
              )}
              {heading !== null && (
                <g transform={`rotate(${heading} ${pt.x} ${pt.y})`}>
                  {/* Wedge: 60° fan extending 40px from the marker, pointing
                      "up" pre-rotation (i.e. 0°=north before the rotate
                      transform applies the device heading). Light grayscale
                      gradient so it reads as direction, not decoration. */}
                  <defs>
                    <radialGradient id="gb-heading-fan" cx="50%" cy="100%" r="100%">
                      <stop offset="0%" stopColor="rgba(14,14,14,0.55)" />
                      <stop offset="60%" stopColor="rgba(14,14,14,0.18)" />
                      <stop offset="100%" stopColor="rgba(14,14,14,0)" />
                    </radialGradient>
                  </defs>
                  <path
                    d={`M ${pt.x} ${pt.y} L ${pt.x - 18} ${pt.y - 38} A 42 42 0 0 1 ${pt.x + 18} ${pt.y - 38} Z`}
                    fill="url(#gb-heading-fan)"
                  />
                </g>
              )}
              {/* Double-circle + center-dot — same shape across both modes.
                  Outer circle r=12, inner r=4, center r=2. Stroke weight
                  and color differ slightly between modes for clarity. */}
              <circle
                cx={pt.x}
                cy={pt.y}
                r="12"
                fill="none"
                stroke="#0E0E0E"
                strokeWidth="1.25"
              />
              <circle
                cx={pt.x}
                cy={pt.y}
                r="4"
                fill="none"
                stroke="#0E0E0E"
                strokeWidth="1.25"
              />
              <circle cx={pt.x} cy={pt.y} r="2" fill="#0E0E0E" />
            </g>
          );
        })()}

        {/* 2026-05-30 admin-only learner dots:工作員「顯示學員」開關。
            有別於 pin(實心黑圓 + 編號)—— 學員點是「空心圓 + 中心點 + 姓名
            小標」,半透明灰,跟 pin / 自己的 GPS crosshair 區隔。純灰階。 */}
        {learnerDots?.map((d) => {
          const pt = project(d.lat, d.lng);
          if (!pt) return null;
          return (
            <g key={`learner-${d.userId}`}>
              <circle
                cx={pt.x}
                cy={pt.y}
                r="6"
                fill="rgba(250,250,250,0.85)"
                stroke="#6B6B6B"
                strokeWidth="1.5"
              />
              <circle cx={pt.x} cy={pt.y} r="2" fill="#6B6B6B" />
              {/* 姓名 + 「幾分前回報」相對時間。時間用更小更淡的字、
                  排在姓名下一行,工作員一眼看出這個點新不新鮮。 */}
              <text
                x={pt.x + 9}
                y={d.ago ? pt.y + 0.5 : pt.y + 3.5}
                fontFamily="'Noto Sans TC', sans-serif"
                fontSize="10"
                fill="#3A3A3A"
                style={{ paintOrder: "stroke", stroke: "#FAFAFA", strokeWidth: 2.5 }}
              >
                {d.name}
              </text>
              {d.ago && (
                <text
                  x={pt.x + 9}
                  y={pt.y + 11}
                  fontFamily="'Space Mono', monospace"
                  fontSize="8"
                  fill="#8A8A8A"
                  style={{ paintOrder: "stroke", stroke: "#FAFAFA", strokeWidth: 2.5 }}
                >
                  {d.ago}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Map controls column. Position differs by mode:
            - user:  bottom-right, lifted above whatever bar / nav the
                     consumer is rendering. Defaults to the legacy 196px
                     magic; consumers running the PlatformBar can pass
                     `userControlsBottom="calc(var(--bar-h-rail) + 12px)"`
                     (or similar per current mode) so the buttons follow
                     the bar height without a Leaflet container resize.
            - admin: top-right, just under the region picker (z 90px),
                     because the PinForm panel below is variable-height
                     and would always block bottom-anchored controls.
          Order top→bottom: zoom in, zoom out, recenter. */}
      <div
        style={{
          position: "absolute",
          right: 12,
          ...(mode === "admin"
            ? { top: "calc(env(safe-area-inset-top, 0px) + 96px)" }
            : { bottom: userControlsBottom }),
          display: "flex",
          flexDirection: "column",
          gap: 2,
          zIndex: 9,
          transition: reducedMotion
            ? "none"
            : "bottom 480ms var(--ease-mist)",
        }}
      >
        {(["+", "−"] as const).map((label, i) => (
          <button
            key={label}
            onClick={() => {
              const m = mapRef.current;
              if (!m) return;
              if (i === 0) m.zoomIn();
              else m.zoomOut();
            }}
            style={{
              width: 44,
              height: 44,
              padding: 0,
              fontFamily: T.mono,
              fontSize: 20,
              lineHeight: 1,
              color: T.ink900,
              background: "rgba(250,250,250,0.92)",
              border: `1px solid ${T.ink200}`,
              cursor: "pointer",
              touchAction: "manipulation",
            }}
            aria-label={i === 0 ? "放大" : "縮小"}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => {
            const m = mapRef.current;
            if (!m) return;
            // Caller hook fires first — useful for iOS to request
            // DeviceOrientation permission on this user gesture, AND
            // for admin to switch region to wherever the worker's GPS
            // lives (MapMark wires that). After the callback, the
            // active-region's center / gps state may have changed, so
            // we read gps fresh and prefer it over region center for
            // both modes.
            onRecenterTap?.();
            const target = gps
              ? L.latLng(gps.lat, gps.lng)
              : L.latLng(regionCenterLat, regionCenterLng);
            m.panTo(target, { animate: true, duration: 0.6 });
          }}
          style={{
            // Locate button: white disc + thin ink border + black arrow
            // (2026-05-30 organizer: the solid-dark fill felt too heavy /
            // off the grayscale-paper aesthetic). Round (lone --radius:0
            // exception — reads as a distinct "find me" affordance).
            width: 48,
            height: 48,
            padding: 0,
            marginTop: 8,
            background: T.paper,
            border: `1px solid ${T.ink900}`,
            borderRadius: "50%",
            cursor: "pointer",
            touchAction: "manipulation",
            display: "grid",
            placeItems: "center",
          }}
          aria-label={gps ? "回到我的位置" : "回到中心"}
        >
          {/* Tilted navigation arrow (paper-plane), like Google Maps' locate
              control. Black ink arrow on the white disc. */}
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
            <path
              d="M12 2 L20 21 L12 16 L4 21 Z"
              fill={T.ink900}
              stroke={T.paper}
              strokeWidth="1"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Corner HUD strips */}
      {mode === "admin" && gps && (
        <div
          style={{
            position: "absolute",
            left: 18,
            bottom: 14,
            pointerEvents: "none",
            zIndex: 8,
            fontFamily: T.mono,
            fontSize: 10,
            letterSpacing: "0.18em",
            color: T.ink500,
            textTransform: "uppercase",
          }}
        >
          {gps.lat.toFixed(4)} / {gps.lng.toFixed(4)}
        </div>
      )}
      {mode === "user" && (
        <div
          style={{
            position: "absolute",
            left: 18,
            bottom: 14,
            pointerEvents: "none",
            zIndex: 8,
            fontFamily: T.han,
            fontSize: 11,
            color: T.ink500,
          }}
        >
          {visibleMarkers.length > 0 ? "你 · 你要找的東西" : "你的碎片還未出現"}
        </div>
      )}
      {mode === "admin" && (
        <div
          style={{
            position: "absolute",
            right: 18,
            bottom: 14,
            pointerEvents: "none",
            zIndex: 8,
            fontFamily: T.mono,
            fontSize: 10,
            letterSpacing: "0.18em",
            color: T.ink350,
            textTransform: "uppercase",
          }}
        >
          {visibleMarkers.length} 已藏
        </div>
      )}
      <div
        style={{
          position: "absolute",
          right: 18,
          top: 14,
          pointerEvents: "none",
          zIndex: 8,
          fontFamily: T.han,
          fontSize: 11,
          color: T.ink500,
          letterSpacing: "0.2em",
        }}
      >
        ↑ 北
      </div>
    </div>
  );
}

/** Haversine distance in meters between two lat/lng points. */
export function metersBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(s));
}
